import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Network, Pencil, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { Modal, ConfirmModal } from '../ui/Modal';
import { useLinkGraph } from '../../graph/useLinkGraph';
import { bumpGraph } from '../../graph/graphUiStore';
import { setGraphProject } from '../../graph/graphProjectScope';
import {
  addToProject,
  canWriteScope,
  deleteProject,
  linkRepoToProject,
  listRepos,
  listBrokenRepos,
  projectAccepts,
  randomProjectGradient,
  removeFromProject,
  saveProject,
  scopeKeyOfWire,
  unlinkRepoFromProject,
} from '../../graph/projectsStore';
import { teamIdOfScope } from '../../graph/graphScopes';
import LinkRepoModal from './LinkRepoModal';
import { useTeamsStore } from '../../store/teamsStore';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { useScopeLabel } from '../graph/GraphScopePicker';
import {
  useTabsStore,
  selectCurrentProjectsTab,
  openProjectsProjectTab,
  updateProjectsTabLabel,
  closeProjectsProjectTab,
} from '../../store/tabsStore';
import { setActiveTab } from '../../store/uiModalStore';
import { takeProjectCreate, useProjectCreateRequest } from '../../scope/projectCreateRequest';
import { TYPE_STYLE } from '../graph/graphTheme';
import { ProjectDetail } from './ProjectsView';
import OsScroll from '../ui/OsScroll';
import SshHostChoiceModal from '../graph/SshHostChoiceModal';
import { stopRunsUnder } from './run/runStore';
import { localPathOf } from '../../services/git/codeRepoClones';
import '../TaskBoardTab.css';
import '../boards/tokens.css';
import '../boards/BoardsHome.css';
import '../boards/BoardTile.css';
import '../boards/BoardFilterBar.css';
import '../Graph.css';

/**
 * Projects' tab content — the Boards shape (owner, 13.09.2026: "open like the
 * boards do"). One instance rendered by EditorPane for every 'projects-project'
 * tab: the pinned "Projects" tab (projectId null) shows the card grid, any other
 * tab shows that project. Cards reuse the board tile styles (boards/BoardTile.css).
 */
export default function ProjectsTab() {
  const { t } = useTranslation();
  const tab = useTabsStore(selectCurrentProjectsTab);
  const { index, projects, links, loading } = useLinkGraph();
  const [filter, setFilter] = useState('');
  const [modal, setModal] = useState(null); // { mode, id?, name, description, color }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [linkingRepo, setLinkingRepo] = useState(false);
  const [error, setError] = useState(null);
  const teams = useTeamsStore((s) => s.teams);
  // D8: a new project can belong to a team only where the user may write
  // (not a viewer). No such team → no Owner field at all.
  const ownerTeams = useMemo(
    () => (teams || []).filter((team) => team?.id != null && canWriteScope(`team:${team.id}`)),
    [teams]
  );

  // Repo links whose saved address is no valid reference (a token or password
  // in it): no graph node, so they are listed here to be removed (05.10.2026).
  const [brokenRepos, setBrokenRepos] = useState(() => new Map());
  const membersByProject = useMemo(() => {
    const map = new Map();
    for (const l of links) {
      if (l.rel !== 'contains' || !l.from.startsWith('project:')) continue;
      const broken = !index.byId.get(l.to) && brokenRepos.get(l.to);
      const node = index.byId.get(l.to) || (broken ? { id: l.to, type: 'git_repo', label: broken.name || broken.host || 'Repository', host: broken.host, needsFix: true } : null);
      if (!node) continue; // member deleted, or not loaded (vault locked)
      const pid = l.from.slice('project:'.length);
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(node);
    }
    return map;
  }, [links, index, brokenRepos]);

  const run = useCallback(async (fn) => {
    setError(null);
    try {
      await fn();
      bumpGraph();
    } catch (err) {
      setError(err?.code ? t(`projects.error.${err.code}`) : err?.message || String(err));
    }
  }, [t]);

  const openCreate = () =>
    setModal({ mode: 'create', name: '', description: '', color: randomProjectGradient(), owner: 'personal' });
  const openEdit = (p) => setModal({ mode: 'edit', ...p });

  // "New project…" from the toolbar project switcher (project scope card 1.2).
  const createNonce = useProjectCreateRequest((s) => s.nonce);
  useEffect(() => {
    if (takeProjectCreate()) openCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per request
  }, [createNonce]);

  const submitModal = () =>
    run(async () => {
      if (!modal?.name.trim()) return;
      const { mode, owner, scopeKey, ...draft } = modal;
      // A new project goes where the Owner field says; an edit stays where it is.
      const saved = await saveProject(draft, mode === 'create' ? scopeKeyOfWire(owner) : scopeKey);
      setModal(null);
      // Owner: creating a project keeps you on the Projects home — the new card
      // appears in the grid; it is not opened in a tab.
      if (mode !== 'create') updateProjectsTabLabel(saved.id, saved.name);
    });

  const confirmDelete = () =>
    run(async () => {
      const target = deleteTarget;
      setDeleteTarget(null);
      await deleteProject(target);
      closeProjectsProjectTab(target.id);
    });

  const openInGraph = (projectId) => {
    setGraphProject(projectId); // the shared toolbar scope (card 6.2)
    setActiveTab('graph.panel');
  };

  const project = tab?.projectId ? projects.find((p) => p.id === tab.projectId) : null;

  // The repo records of the open project's scope: the graph node carries no
  // description or branch (graph privacy rule), the project page shows both.
  const [repoInfo, setRepoInfo] = useState(() => new Map());
  const projectScope = project?.scopeKey;
  useEffect(() => {
    if (!projectScope) return undefined;
    let live = true;
    listRepos(projectScope)
      .then((repos) => live && setRepoInfo(new Map(repos.map((r) => [`git_repo:${r.id}`, r]))))
      .catch(() => {});
    listBrokenRepos(projectScope)
      .then((rows) => live && setBrokenRepos(new Map(rows.map((r) => [`git_repo:${r.id}`, r]))))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [projectScope, links]);

  const modals = (
    <>
      <SshHostChoiceModal />
      {modal && (
        <Modal isOpen onClose={() => setModal(null)} title={modal.mode === 'create' ? t('projects.createTitle') : t('projects.editTitle')}>
          <form
            className="flex flex-col"
            style={{ gap: '0.875rem' }}
            onSubmit={(e) => {
              e.preventDefault();
              submitModal();
            }}
            data-testid="project-editor"
          >
            <div>
              <label className="modal-logo-label" htmlFor="project-name" style={{ display: 'block', marginBottom: '0.375rem' }}>
                {t('projects.name')}
              </label>
              <input
                id="project-name"
                type="text"
                autoFocus
                className="modal-input modal-input-flat"
                placeholder={t('projects.namePlaceholder')}
                value={modal.name}
                onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="modal-logo-label" htmlFor="project-description" style={{ display: 'block', marginBottom: '0.375rem' }}>
                {t('projects.description')}
              </label>
              <textarea
                id="project-description"
                className="modal-input modal-input-flat"
                style={{ resize: 'vertical' }}
                rows={2}
                placeholder={t('projects.descriptionPlaceholder')}
                value={modal.description}
                onChange={(e) => setModal((m) => ({ ...m, description: e.target.value }))}
              />
            </div>
            {modal.mode === 'create' && ownerTeams.length > 0 && (
              <div>
                <span className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
                  {t('projects.owner')}
                </span>
                <CompactSelectPicker
                  className="graph-picker graph-picker--grow"
                  menuClassName="graph-picker-menu"
                  value={modal.owner}
                  onChange={(owner) => setModal((m) => ({ ...m, owner: owner || 'personal' }))}
                  options={[
                    { value: 'personal', label: t('projects.ownerPersonal') },
                    ...ownerTeams.map((team) => ({
                      value: `team:${team.id}`,
                      label: team.name ? t('graph.scope.team', { name: team.name }) : t('graph.scope.teamUnnamed'),
                    })),
                  ]}
                  aria-label={t('projects.owner')}
                />
              </div>
            )}
            <div>
              <span className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
                {t('projects.color')}
              </span>
              <div className="flex items-center gap-2">
                <div style={{ background: modal.color, width: '100%', height: '2.25rem', borderRadius: '0.5rem' }} />
                <button
                  type="button"
                  className="task-board-icon-btn"
                  title={t('projects.newGradient')}
                  aria-label={t('projects.newGradient')}
                  onClick={() => setModal((m) => ({ ...m, color: randomProjectGradient() }))}
                >
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>
            <div className="modal-footer-row">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={!modal.name.trim()}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
              >
                {modal.mode === 'create' ? t('common.create') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t('projects.deleteTitle')}
        message={t('projects.deleteConfirm', { name: deleteTarget?.name })}
        confirmText={t('common.delete')}
        type="danger"
      />
    </>
  );

  // ── one project ──────────────────────────────────────────────────────────
  if (tab?.projectId) {
    if (!project) {
      return (
        <div className="task-board flex flex-col h-full min-h-0">
          <div className="task-board-empty">{loading ? <span className="env-save-spinner" /> : t('projects.notFound')}</div>
        </div>
      );
    }
    return (
      <div className="task-board flex flex-col h-full min-h-0" data-testid="project-tab">
        {error && <div className="graph-error graph-error--bar">{error}</div>}
        <OsScroll className="graph-view projects-body">
          <ProjectDetail
            project={project}
            members={membersByProject.get(project.id) || []}
            index={index}
            onAdd={(nodeId) => run(() => addToProject(project, index.byId.get(nodeId) || nodeId))}
            onRemove={(nodeId) =>
              run(async () => {
                if (!nodeId.startsWith('git_repo:')) return removeFromProject(project, nodeId);
                // Its running configurations go with it: no row would be left to stop them.
                await stopRunsUnder(localPathOf(repoInfo.get(nodeId)));
                return unlinkRepoFromProject(project, nodeId.slice('git_repo:'.length));
              })
            }
            onLinkRepo={() => setLinkingRepo(true)}
            repoInfo={repoInfo}
            canAdd={(node) => projectAccepts(project, node)}
            readOnly={!canWriteScope(project.scopeKey)}
            onError={(err) => run(() => Promise.reject(err))}
            onOpenInGraph={() => openInGraph(project.id)}
            onEdit={() => openEdit(project)}
            onDelete={() => setDeleteTarget(project)}
          />
        </OsScroll>
        {modals}
        {linkingRepo && (
          <LinkRepoModal
            allowFolder={!teamIdOfScope(project.scopeKey)}
            onClose={() => setLinkingRepo(false)}
            onSubmit={(draft) =>
              run(async () => {
                await linkRepoToProject(project, { remote: draft.url, ...draft });
                setLinkingRepo(false);
              })
            }
          />
        )}
      </div>
    );
  }

  // ── pinned home: the card grid ───────────────────────────────────────────
  const q = filter.trim().toLowerCase();
  const filtered = projects.filter((p) => p.name.toLowerCase().includes(q));

  return (
    <div className="task-board flex flex-col h-full min-h-0" data-testid="projects-home">
      {loading && projects.length === 0 ? (
        <div className="task-board-empty">
          <span className="env-save-spinner" />
        </div>
      ) : (
        <OsScroll className="task-board-home projects-home-scroll">
          <div className="task-board-home-search">
            <div className="task-board-filter-search">
              <Search size={13} aria-hidden />
              <input
                type="text"
                value={filter}
                placeholder={t('projects.filterPlaceholder')}
                aria-label={t('projects.filterPlaceholder')}
                autoComplete="off"
                onChange={(e) => setFilter(e.target.value)}
              />
              {!!filter && (
                <button type="button" aria-label={t('common.close')} onClick={() => setFilter('')}>
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          {error && <div className="graph-error">{error}</div>}
          {projects.length > 0 && filtered.length === 0 ? (
            <div className="task-board-empty">{t('projects.noMatch')}</div>
          ) : (
            <div className="task-board-grid">
              {filtered.map((p) => (
                <ProjectTile
                  key={p.id}
                  project={p}
                  members={membersByProject.get(p.id) || []}
                  onOpen={() => openProjectsProjectTab({ projectId: p.id, label: p.name })}
                  onGraph={() => openInGraph(p.id)}
                  onEdit={canWriteScope(p.scopeKey) ? () => openEdit(p) : null}
                  onDelete={canWriteScope(p.scopeKey) ? () => setDeleteTarget(p) : null}
                />
              ))}
              <button type="button" className="task-board-tile task-board-tile--new" onClick={openCreate}>
                <span className="task-board-tile-new-icon" aria-hidden>
                  <Plus size={18} />
                </span>
                <span className="task-board-tile-new-label">{t('projects.new')}</span>
              </button>
            </div>
          )}
        </OsScroll>
      )}
      {modals}
    </div>
  );
}

function ProjectTile({ project, members, onOpen, onGraph, onEdit, onDelete }) {
  const { t } = useTranslation();
  const team = useScopeLabel()(project);
  const counts = {};
  for (const m of members) counts[m.type] = (counts[m.type] || 0) + 1;
  const summary = Object.keys(TYPE_STYLE)
    .filter((type) => counts[type])
    .map((type) => t('projects.memberCount', { type: t(TYPE_STYLE[type].labelKey), count: counts[type] }))
    .join(' · ');
  const act = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };
  return (
    <div
      className="task-board-tile"
      role="button"
      tabIndex={0}
      aria-label={project.name}
      style={{ background: project.color }}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
    >
      <div className="task-board-tile-top">
        <span className="task-board-tile-label">{team ? `${t('graph.type.project')} · ${team}` : t('graph.type.project')}</span>
        <div className="task-board-tile-actions">
          <button type="button" className="task-board-tile-action-btn" title={t('projects.openInGraph')} aria-label={t('projects.openInGraph')} onClick={act(onGraph)}>
            <Network size={12} />
          </button>
          {/* A team viewer sees the card, not the controls (D8). */}
          {onEdit && (
            <button type="button" className="task-board-tile-action-btn" title={t('projects.editTitle')} aria-label={t('projects.editTitle')} onClick={act(onEdit)}>
              <Pencil size={12} />
            </button>
          )}
          {onDelete && (
            <button type="button" className="task-board-tile-action-btn" title={t('projects.deleteTitle')} aria-label={t('projects.deleteTitle')} onClick={act(onDelete)}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
      <div>
        <div className="task-board-tile-name">{project.name}</div>
        {/* Like a board's "3 tasks": one short count; the per-type breakdown is the tooltip. */}
        <div className="task-board-tile-meta" title={summary || undefined}>
          {members.length === 0 ? t('projects.noMembers') : t('projects.itemCount', { count: members.length })}
        </div>
        {project.description ? <div className="task-board-tile-desc">{project.description}</div> : null}
      </div>
    </div>
  );
}
